import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Sparkles, Send, ChevronDown, Loader2, FileEdit, Paperclip, X } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

const AI_ACTIONS = [
  { key: "generate_reply", label: "Generate reply" },
  { key: "rewrite_professional", label: "Rewrite professionally" },
  { key: "make_concise", label: "Make concise" },
  { key: "make_friendly", label: "Make friendly" },
  { key: "make_firm", label: "Make firm" },
  { key: "make_formal", label: "Make formal" },
  { key: "correct_grammar", label: "Correct grammar" },
  { key: "generate_subject", label: "Generate subject" },
  { key: "generate_follow_up", label: "Follow-up email" },
  { key: "generate_apology", label: "Apology email" },
  { key: "generate_payment_reminder", label: "Payment reminder" },
];

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE = 15 * 1024 * 1024;

function htmlToText(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  return tmp.textContent || tmp.innerText || "";
}

export default function Composer({ open, onClose, prefill, onSent }) {
  const { activeCompanyId } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    account_id: "",
    to: "", cc: "", bcc: "",
    subject: "",
    body_html: "",
    in_reply_to_message_id: "",
  });
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiConfidence, setAiConfidence] = useState(0);
  const [signatures, setSignatures] = useState([]);
  const [signatureId, setSignatureId] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      (async () => {
        const params = activeCompanyId ? { company_id: activeCompanyId } : {};
        const { data } = await api.get("/email-accounts", { params });
        setAccounts(data);
        if (prefill) {
          setForm({
            account_id: prefill.account_id || data[0]?.id || "",
            to: (prefill.to || []).join(", "),
            cc: (prefill.cc || []).join(", "),
            bcc: (prefill.bcc || []).join(", "),
            subject: prefill.subject || "",
            body_html: prefill.body_html || (prefill.body_text ? prefill.body_text.replace(/\n/g, "<br>") : ""),
            in_reply_to_message_id: prefill.in_reply_to_message_id || "",
          });
        } else {
          setForm((f) => ({ ...f, account_id: data[0]?.id || "" }));
        }
        setAttachments([]);
        setAiConfidence(0);
        // load signatures for this company/account
        try {
          const params = { ...(activeCompanyId ? { company_id: activeCompanyId } : {}), account_id: (prefill?.account_id || data[0]?.id) };
          const { data: sigs } = await api.get("/signatures", { params });
          setSignatures(sigs);
          const defaultSig = sigs.find((s) => s.is_default_user) || sigs.find((s) => !s.account_id && s.is_default_user);
          setSignatureId(defaultSig?.id || "");
        } catch { /* ignore */ }
      })();
    }
  }, [open, prefill, activeCompanyId]);

  const runAI = async (instruction) => {
    setAiBusy(true);
    try {
      const bodyText = htmlToText(form.body_html);
      const payload = {
        instruction,
        message_id: form.in_reply_to_message_id || null,
        draft_text: bodyText || null,
        context: bodyText || null,
      };
      const { data } = await api.post("/ai/compose", payload);
      if (instruction === "generate_subject" && data.subject) {
        setForm((f) => ({ ...f, subject: data.subject }));
      } else {
        const html = (data.text || "").split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
        setForm((f) => ({ ...f, body_html: html }));
      }
      setAiConfidence(data.confidence || 0);
      toast.success("AI generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    }
    setAiBusy(false);
  };

  const parseAddrs = (s) => s.split(/[,\s;]+/).map((x) => x.trim()).filter(Boolean);

  const uploadAttachment = async (file) => {
    if (attachments.length >= MAX_ATTACHMENTS) return toast.error(`Max ${MAX_ATTACHMENTS} attachments`);
    if (file.size > MAX_FILE_SIZE) return toast.error(`${file.name} exceeds 15MB`);
    const acct = accounts.find((a) => a.id === form.account_id);
    if (!acct) return toast.error("Choose a From account first");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/attachments/upload?company_id=${acct.company_id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, data]);
      toast.success(`Attached ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const removeAttachment = (idx) => setAttachments((a) => a.filter((_, i) => i !== idx));

  const submit = async (saveDraft) => {
    if (!form.account_id) { toast.error("Select a sending account"); return; }
    setSending(true);
    try {
      const bodyText = htmlToText(form.body_html);
      const payload = {
        account_id: form.account_id,
        to: parseAddrs(form.to),
        cc: parseAddrs(form.cc),
        bcc: parseAddrs(form.bcc),
        subject: form.subject,
        body_text: bodyText,
        body_html: form.body_html,
        in_reply_to_message_id: form.in_reply_to_message_id || null,
        save_as_draft: saveDraft,
        attachments,
        signature_id: signatureId || null,
      };
      await api.post("/emails/send", payload);
      toast.success(saveDraft ? "Saved as draft" : "Email sent");
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="sm:max-w-[760px] w-full flex flex-col p-0 border-l border-border" data-testid="composer-sheet">
        <SheetHeader className="border-b border-border p-4">
          <SheetTitle className="font-display text-xl tracking-tight flex items-center gap-2">
            <Send className="h-4 w-4 text-emerald-brand" /> Compose
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <F label="From">
            <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
              <SelectTrigger className="rounded-xl" data-testid="composer-from-select"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.email_address}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="To"><Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="composer-to-input" className="rounded-xl" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Cc"><Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} data-testid="composer-cc-input" className="rounded-xl" /></F>
            <F label="Bcc"><Input value={form.bcc} onChange={(e) => setForm({ ...form, bcc: e.target.value })} data-testid="composer-bcc-input" className="rounded-xl" /></F>
          </div>
          <F label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="composer-subject-input" className="rounded-xl" /></F>

          <F label="Signature">
            <Select value={signatureId || "none"} onValueChange={(v) => setSignatureId(v === "none" ? "" : v)}>
              <SelectTrigger className="rounded-xl" data-testid="composer-signature-select"><SelectValue placeholder="No signature" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No signature</SelectItem>
                {signatures.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_default_user ? " (default)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Message</Label>
              <div className="flex items-center gap-2">
                {aiConfidence > 0 && (
                  <span className="text-[10px] font-mono text-emerald-brand">AI confidence {Math.round(aiConfidence * 100)}%</span>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="rounded-lg h-8" disabled={aiBusy} data-testid="ai-menu-trigger">
                      {aiBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 text-sun mr-1.5" />}
                      AI assist <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-xl w-56">
                    <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest">AI actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {AI_ACTIONS.map((a) => (
                      <DropdownMenuItem key={a.key} onClick={() => runAI(a.key)} data-testid={`ai-action-${a.key}`}>{a.label}</DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <div className={aiBusy ? "opacity-60 pointer-events-none" : ""}>
              <RichTextEditor value={form.body_html} onChange={(v) => setForm({ ...form, body_html: v })} placeholder="Write your message…" />
            </div>
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Attachments ({attachments.length}/{MAX_ATTACHMENTS})</Label>
              <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => fileInputRef.current?.click()} data-testid="add-attachment-button">
                <Paperclip className="h-4 w-4 mr-1.5" /> Add file
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f); e.target.value = ""; }}
                data-testid="attachment-input"
              />
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 border border-border rounded-lg px-2.5 py-1.5 bg-muted/40 text-xs" data-testid={`attachment-${i}`}>
                    <Paperclip className="h-3 w-3 text-emerald-brand" />
                    <span className="max-w-[180px] truncate">{a.filename}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{Math.round((a.size || 0) / 1024)}KB</span>
                    <button onClick={() => removeAttachment(i)} className="text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-border p-4 flex items-center justify-between gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => submit(true)} disabled={sending} data-testid="composer-draft-button">
            <FileEdit className="h-4 w-4 mr-1.5" /> Save draft
          </Button>
          <Button className="rounded-xl shadow-md shadow-primary/20" onClick={() => submit(false)} disabled={sending} data-testid="composer-send-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
            Send email
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
